/**
 * Generic styled PDF export for tabular reports — @react-pdf/renderer, A4 landscape.
 *
 * Mirrors cari-movements-pdf.tsx: Roboto font (Turkish glyph support), letterhead,
 * params strip, navy header, zebra rows, emphasis styling for group/subtotal/total,
 * right-aligned numeric cells with tr-TR formatting, page footer.
 *
 * Driven entirely by ReportTableDoc so any columnar report can reuse this.
 */
import React from 'react';
import { Document, Page, Text, View, Image, StyleSheet, Font, pdf } from '@react-pdf/renderer';
import type { ReportTableDoc, ReportColumn } from './report-table-types';

// ── Font registration (mirrors cari-movements-pdf.tsx exactly) ────────────────
export function registerTableReportFonts(basePath = '/fonts'): void {
  Font.register({
    family: 'Roboto',
    fonts: [
      { src: `${basePath}/Roboto-Regular.ttf` },
      { src: `${basePath}/Roboto-Medium.ttf`, fontWeight: 500 },
      { src: `${basePath}/Roboto-Bold.ttf`, fontWeight: 700 },
    ],
  });
}
if (typeof window !== 'undefined') registerTableReportFonts();
Font.registerHyphenationCallback((word) => [word]);

// ── Colour palette (mirrors cari-movements-pdf.tsx) ───────────────────────────
const C = {
  ink: '#111827',
  inkSoft: '#4b5563',
  inkFaint: '#9ca3af',
  navy: '#1e3a5f',
  navySoft: '#eef2f7',
  navyGroup: '#d6e0ed',
  zebra: '#fafbfc',
  line: '#e5e7eb',
  lineSoft: '#f1f5f9',
  white: '#ffffff',
};

// ── Shared numeric formatter ───────────────────────────────────────────────────
const numFmt = new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function fmtNum(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isNaN(n) ? '' : numFmt.format(n);
}

// ── Styles ────────────────────────────────────────────────────────────────────
const s = StyleSheet.create({
  page: {
    paddingTop: 30,
    paddingBottom: 46,
    paddingHorizontal: 28,
    fontSize: 8,
    fontFamily: 'Roboto',
    color: C.ink,
  },

  // Letterhead
  letterhead: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingBottom: 12,
    marginBottom: 12,
    borderBottomWidth: 2,
    borderBottomColor: C.navy,
  },
  brand: { flexDirection: 'row', alignItems: 'center' },
  logo: { width: 76, height: 38, objectFit: 'contain', marginRight: 10 },
  companyBlock: { flexDirection: 'column' },
  companyName: { fontSize: 14, fontWeight: 700, color: C.navy, letterSpacing: 0.4 },
  companyLine: { fontSize: 7.5, color: C.inkSoft, marginTop: 1.5 },
  reportMeta: { alignItems: 'flex-end' },
  reportTitle: { fontSize: 13, fontWeight: 700, color: C.navy, letterSpacing: 0.6 },
  reportGenerated: { fontSize: 7, color: C.inkFaint, marginTop: 3 },

  // Parameter strip
  paramStrip: {
    backgroundColor: C.navySoft,
    paddingVertical: 5,
    paddingHorizontal: 8,
    marginBottom: 10,
    borderLeftWidth: 3,
    borderLeftColor: C.navy,
  },
  paramItem: { fontSize: 7.5, color: C.inkSoft, marginVertical: 1 },

  // Table header
  headerRow: {
    flexDirection: 'row',
    backgroundColor: C.navy,
    paddingVertical: 4,
    paddingHorizontal: 6,
  },
  headerCell: { fontSize: 7.5, fontWeight: 700, color: C.white },

  // Data rows
  row: {
    flexDirection: 'row',
    paddingVertical: 3,
    paddingHorizontal: 6,
    borderBottomWidth: 0.5,
    borderBottomColor: C.lineSoft,
  },
  rowAlt: { backgroundColor: C.zebra },
  rowGroup: { backgroundColor: C.navyGroup },
  rowSubtotal: { borderTopWidth: 0.5, borderTopColor: C.line },
  rowTotal: { backgroundColor: C.navySoft },

  cell: { fontSize: 7.5 },
  cellMuted: { color: C.inkSoft },
  cellBold: { fontWeight: 700 },
  cellNavy: { color: C.navy },
  numCell: { textAlign: 'right' },

  // Inline thumbnail cell (price-list "include images" option)
  imageCell: { alignItems: 'center', justifyContent: 'center', paddingVertical: 1 },
  thumb: { width: 34, height: 34, objectFit: 'contain' },

  // ── 'sections' layout overrides ─────────────────────────────────────────────
  // Requested for the price list: readers found the colour-banded group rows
  // (product identity on a blue band, lots on white) hard to follow. Sections
  // layout renders each product as a white section title with a strong rule
  // above it, larger and darker type throughout, and reserves fills for the
  // summary rows only.
  // Horizontal padding doubles as the column gutter: a right-aligned number
  // followed by a left-aligned text column would otherwise render glued
  // ("27,61EUR").
  secHeaderCell: { fontSize: 8.5, paddingHorizontal: 3 },
  secRow: { paddingVertical: 4.5 },
  secCell: { fontSize: 8.5, color: C.ink, paddingHorizontal: 3 },
  secRowGroup: {
    backgroundColor: C.white,
    borderTopWidth: 1.5,
    borderTopColor: C.navy,
    marginTop: 8,
    paddingTop: 6,
    paddingBottom: 4,
  },
  secGroupCell: { fontSize: 9.5 },
  secRowSubtotal: { backgroundColor: '#f3f4f6', borderTopWidth: 0.5, borderTopColor: C.line },
  secRowTotal: { backgroundColor: C.navySoft, borderTopWidth: 1, borderTopColor: C.navy },

  // Footer
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 28,
    right: 28,
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderTopWidth: 0.5,
    borderTopColor: C.line,
    paddingTop: 6,
  },
  footerText: { fontSize: 7, color: C.inkFaint },
});

// ── Column flex width ─────────────────────────────────────────────────────────
// Convert width-hint (xlsx char units) to a flex ratio string for react-pdf.
// Default widths: numeric = 12, text = 16. We use these as relative flex values.
function colFlex(col: ReportColumn): { flex: number } {
  return { flex: col.width ?? (col.num ? 12 : 16) };
}

function colTextAlign(col: ReportColumn): 'left' | 'right' | 'center' {
  if (col.num) return 'right';
  return col.align ?? 'left';
}

// ── Component ─────────────────────────────────────────────────────────────────
export function TableReportPdf({ doc }: { doc: ReportTableDoc }): React.JSX.Element {
  const { company, title, params, generatedLabel, columns, rows } = doc;
  const sectioned = doc.layout === 'sections';

  const headerRow = (
    <View style={s.headerRow} fixed>
      {columns.map((col) => (
        <Text
          key={col.key}
          style={[
            s.headerCell,
            sectioned ? s.secHeaderCell : undefined,
            colFlex(col),
            { textAlign: colTextAlign(col) },
          ].filter(Boolean) as Array<(typeof s)[keyof typeof s]>}
        >
          {col.header}
        </Text>
      ))}
    </View>
  );

  let normalRowIndex = 0; // only counts non-emphasis rows for zebra striping

  const renderRow = (row: ReportTableDoc['rows'][number], i: number) => {
    const isEmphasis = row.emphasis != null;
    const isZebra = !isEmphasis && normalRowIndex % 2 === 1;
    if (!isEmphasis) normalRowIndex++;

    const rowStyle = [
      s.row,
      sectioned ? s.secRow : undefined,
      isZebra ? s.rowAlt : undefined,
      row.emphasis === 'group'    ? (sectioned ? s.secRowGroup    : s.rowGroup)    : undefined,
      row.emphasis === 'subtotal' ? (sectioned ? s.secRowSubtotal : s.rowSubtotal) : undefined,
      row.emphasis === 'total'    ? (sectioned ? s.secRowTotal    : s.rowTotal)    : undefined,
    ].filter(Boolean);

    return (
      <View key={i} style={rowStyle as Array<(typeof s)[keyof typeof s]>} wrap={false}>
        {columns.map((col) => {
          // Image columns render an inline thumbnail (data URL). A missing/blank
          // value (e.g. on group/total rows, or a product with no image) renders
          // an empty cell — never a broken-image throw.
          if (col.image) {
            const src = row.cells[col.key];
            return (
              <View
                key={col.key}
                style={[s.cell, colFlex(col), s.imageCell] as Array<(typeof s)[keyof typeof s]>}
              >
                {typeof src === 'string' && src.length > 0 ? (
                  <Image src={src} style={s.thumb} />
                ) : null}
              </View>
            );
          }

          const raw = row.cells[col.key];
          const text = col.num ? fmtNum(raw) : (raw != null ? String(raw) : '');

          const cellStyle = [
            s.cell,
            sectioned ? s.secCell : undefined,
            colFlex(col),
            col.num ? s.numCell : (col.align === 'right' ? s.numCell : col.align === 'center' ? { textAlign: 'center' as const } : undefined),
            // Sections layout keeps data cells full-contrast ink; the muted
            // grey belongs to the legacy banded look only.
            !sectioned && !col.num && !row.emphasis ? s.cellMuted : undefined,
            row.emphasis ? s.cellBold : undefined,
            row.emphasis === 'group' || row.emphasis === 'total' ? s.cellNavy : undefined,
            sectioned && row.emphasis === 'group' ? s.secGroupCell : undefined,
          ].filter(Boolean);

          return (
            <Text key={col.key} style={cellStyle as Array<(typeof s)[keyof typeof s]>}>
              {text}
            </Text>
          );
        })}
      </View>
    );
  };

  return (
    <Document title={title} author={company.name}>
      <Page size="A4" orientation="landscape" style={s.page}>
        {/* Letterhead */}
        <View style={s.letterhead}>
          <View style={s.brand}>
            {company.logo && <Image src={company.logo} style={s.logo} />}
            <View style={s.companyBlock}>
              <Text style={s.companyName}>{company.name}</Text>
              {company.address && <Text style={s.companyLine}>{company.address}</Text>}
              {(company.phone || company.email || company.web) && (
                <Text style={s.companyLine}>
                  {[company.phone, company.email, company.web].filter(Boolean).join('  ·  ')}
                </Text>
              )}
            </View>
          </View>
          <View style={s.reportMeta}>
            <Text style={s.reportTitle}>{title}</Text>
            <Text style={s.reportGenerated}>{generatedLabel}</Text>
          </View>
        </View>

        {/* Parameter strip */}
        {params.length > 0 && (
          <View style={s.paramStrip}>
            {params.map((p, i) => (
              <Text key={i} style={s.paramItem}>{p}</Text>
            ))}
          </View>
        )}

        {/* Table header */}
        {headerRow}

        {/* Data rows */}
        {rows.map(renderRow)}

        {/* Footer (every page) */}
        <View style={s.footer} fixed>
          <Text style={s.footerText}>{company.name}  ·  {title}</Text>
          <Text
            style={s.footerText}
            render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
          />
        </View>
      </Page>
    </Document>
  );
}

/** Browser entry point: render to PDF blob and trigger a download. */
export async function downloadTableReportPdf(doc: ReportTableDoc, filename: string): Promise<void> {
  const blob = await pdf(<TableReportPdf doc={doc} />).toBlob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
